import { useEffect, useState } from "react";
import type React from "react";

import XIcon from "assets/icons/x.svg?react";
import css from "./toast.module.scss";

export type ToastVariant = "error" | "info" | "warning";

type ToastProps = {
  title: string;
  message?: string;
  variant?: ToastVariant;
  onClose: () => void;
  autoClose?: number;
};

function ErrorIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM10 6C10.5523 6 11 6.44772 11 7V10C11 10.5523 10.5523 11 10 11C9.44772 11 9 10.5523 9 10V7C9 6.44772 9.44772 6 10 6ZM10 14C10.5523 14 11 13.5523 11 13C11 12.4477 10.5523 12 10 12C9.44772 12 9 12.4477 9 13C9 13.5523 9.44772 14 10 14Z"
        fill="currentColor"
      />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM11 9C11 8.44772 10.5523 8 10 8C9.44772 8 9 8.44772 9 9V13C9 13.5523 9.44772 14 10 14C10.5523 14 11 13.5523 11 13V9ZM10 7C10.5523 7 11 6.55228 11 6C11 5.44772 10.5523 5 10 5C9.44772 5 9 5.44772 9 6C9 6.55228 9.44772 7 10 7Z"
        fill="currentColor"
      />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM11 9C11 8.44772 10.5523 8 10 8C9.44772 8 9 8.44772 9 9V13C9 13.5523 9.44772 14 10 14C10.5523 14 11 13.5523 11 13V9ZM10 7C10.5523 7 11 6.55228 11 6C11 5.44772 10.5523 5 10 5C9.44772 5 9 5.44772 9 6C9 6.55228 9.44772 7 10 7Z"
        fill="currentColor"
      />
    </svg>
  );
}

const VARIANT_CONFIG: Record<
  ToastVariant,
  { className: string; icon: React.ReactNode }
> = {
  info: { className: css.infoIcon, icon: <InfoIcon /> },
  warning: { className: css.warningIcon, icon: <WarningIcon /> },
  error: { className: css.icon, icon: <ErrorIcon /> },
};

export function Toast({
  title,
  message,
  variant = "error",
  onClose,
  autoClose = 5000,
}: ToastProps) {
  const { className: iconClassName, icon } =
    VARIANT_CONFIG[variant] || VARIANT_CONFIG.info;
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (autoClose > 0) {
      const timer = setTimeout(() => setIsVisible(false), autoClose);
      return () => clearTimeout(timer);
    }
  }, [autoClose, onClose]);

  return (
    <div
      className={`${css.toast} ${isVisible ? css.visible : css.hidden}`}
      onTransitionEnd={() => {
        if (!isVisible) onClose();
      }}
    >
      <div className={iconClassName}>{icon}</div>
      <div className={css.content}>
        <p className={css.title}>{title}</p>
        {message && <p className={css.message}>{message}</p>}
      </div>
      <button className={css.closeButton} onClick={() => setIsVisible(false)}>
        <XIcon width={16} height={16} />
      </button>
    </div>
  );
}
